// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { STATUS_CHIP_CONFIG } from '@/billing/components/charge-status-constants';
import {
  computeLineTotal,
  parseFormAmount,
} from '@/billing/utilities/line-item-helpers';
import { StatusChip } from '@/components/status-chip';
import { IsoCurrencyCode } from '@/currency/constants/iso';
import { formatAmountWithCurrency } from '@/currency/utilities/format';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Stack,
  Typography,
} from '@mui/material';

interface RemoveLineItemPreview {
  description: string;
  pricingMode: string;
  quantity: string;
  unitPrice: string | null;
  totalPrice: string | null;
  taxAmount: string | null;
}

interface Props {
  isOpen: boolean;
  preview: RemoveLineItemPreview;
  currencyCode: IsoCurrencyCode;
  onClose: () => void;
  onConfirm: () => void;
}

// Mirrors the dialog preview from the billing-plan-with-invoicing branch.
// Shows the current draft values — removal only affects local form state
// until the user saves the whole table, so the dialog description uses the
// ambiguous "will return to billable status" wording that applies whether
// or not the charge binding ends up being reverted at save time.
export function RemoveLineItemDialog(props: Props) {
  const quantity = Number(props.preview.quantity) || 0;
  const unitPrice =
    props.preview.unitPrice !== null
      ? parseFormAmount(props.preview.unitPrice)
      : null;
  const isPerUnit =
    props.preview.pricingMode === 'PER_UNIT' && unitPrice !== null;
  const summary = isPerUnit
    ? `${quantity} × ${formatAmountWithCurrency(unitPrice, props.currencyCode)}`
    : `${quantity} units — fixed amount`;
  const lineTotal = computeLineTotal(
    props.preview.totalPrice,
    props.preview.taxAmount
  );
  const billableChipProps = STATUS_CHIP_CONFIG.BILLABLE;

  return (
    <Dialog open={props.isOpen} onClose={props.onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Remove line item</DialogTitle>
      <DialogContent>
        <Stack spacing={2}>
          <Typography component="div" variant="body2">
            Remove this line item from the invoice? The underlying charge will
            return to{' '}
            <Box
              component="span"
              sx={{ display: 'inline-flex', verticalAlign: 'middle' }}
            >
              <StatusChip
                label={billableChipProps.label}
                variant={billableChipProps.variant}
                color={billableChipProps.color}
              />
            </Box>{' '}
            status.
          </Typography>
          <Paper
            variant="outlined"
            sx={{ p: 2, backgroundColor: 'action.hover' }}
          >
            <Typography variant="body2" fontWeight="bold">
              {props.preview.description || 'Untitled line item'}
            </Typography>
            <Stack
              direction="row"
              justifyContent="space-between"
              sx={{ mt: 1 }}
            >
              <Typography variant="body2" color="text.secondary">
                {summary}
              </Typography>
              <Typography variant="body2" fontWeight="bold">
                {formatAmountWithCurrency(lineTotal, props.currencyCode)}
              </Typography>
            </Stack>
          </Paper>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={props.onClose}>No, cancel</Button>
        <Button color="error" onClick={props.onConfirm}>
          Yes, remove line item
        </Button>
      </DialogActions>
    </Dialog>
  );
}
